import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useGetEventById } from '../../../../../services/react-query/events';
import { Contact } from '../../../../../types/Contact';
import { EventEmployee } from '../../../../../types/Event';
import { Task } from '../../../../../types/Task';
import { Event } from '../../../../../types/Event';
import StatusPill from '../../../../../uikit/pills/StatusPill';
import { humanizeEventDateTimes, humanizeTimestamp } from '../../../../../utility/dateUtils';
import TaskStatusPill from '../../../../tasks/TaskStatusPill';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../moduleDrawerStyles';
import _ from 'lodash';
import DrawerLink from '../DrawerLink';
import { UserConfigurableModule } from '../../../../../types/User';

type Props = { uuid: string };

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const EventDrawerView: React.FC<Props> = ({ uuid }) => {
  const { data: event, isLoading } = useGetEventById({ uuid });

  if (isLoading) return <CenterSpinner />;
  if (!event)
    return (
      <Typography variant="body2" color="error">
        Event not found
      </Typography>
    );

  const contacts = event.contacts || [];
  const eventEmployees = event.event_employees || [];
  const tasks = event.tasks || [];
  const children = event.children || [];

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(event.display_name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Event Information */}
          <DrawerSection title="Event Information">
            <InfoRow label="Name" value={event.display_name || event.name} />
            {event.multi_child && (
              <InfoRow
                label="Group"
                value={
                  <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.parent_uuid ?? ''}>
                    {event.parent_name}
                  </DrawerLink>
                }
              />
            )}
            <InfoRow label="Account" value={event.account_name} />
            <InfoRow label="Event Type" value={event.type_value} />
            <InfoRow label="Event Location" value={event.location_value} />
            <InfoRow label="Date / Time" value={humanizeEventDateTimes(event)} />
            <InfoRow label="Status" value={<StatusPill status={event.status} />} />
          </DrawerSection>

          {event.multi_parent && (
            <DrawerSection title="Subevents">
              {children.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No subevents found
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {_.map(children, (child: Event) => {
                    return (
                      <Box key={child.uuid} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, borderBottom: '1px solid #eee', py: 1 }}>
                        <DrawerLink module={UserConfigurableModule.EVENTS} uuid={child.uuid}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} title={child.display_name}>
                            {child.name}
                          </Typography>
                        </DrawerLink>

                        <InfoRow label="Type" value={child.type_value} />
                        <InfoRow label="Location" value={child.location_value} />
                        <InfoRow label="Date/Time" value={humanizeEventDateTimes(child)} />
                        <InfoRow label="Status" value={<StatusPill status={child.status} />} />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DrawerSection>
          )}

          {/* Contacts */}
          <DrawerSection title="Contacts">
            {contacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No contacts found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {contacts.map((contact: Contact) => (
                  <Box key={contact.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {contact.full_name}
                    </Typography>
                    <InfoRow label="Email" value={contact.email} />
                    <InfoRow label="Phone" value={contact.phone_1} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Employees */}
          <DrawerSection title="Employees">
            {eventEmployees.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No employees found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {eventEmployees.map((eventEmployee: EventEmployee) => (
                  <Box key={eventEmployee.employee.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {eventEmployee.employee.display_name}
                    </Typography>
                    <InfoRow label="Email" value={eventEmployee.employee.email} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Tasks */}
          <DrawerSection title="Tasks">
            {tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No tasks found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {tasks.map((task: Task) => {
                  return (
                    <Box key={task.uuid} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, borderBottom: '1px solid #eee', py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={task.name}
                      >
                        {task.name}
                      </Typography>

                      <InfoRow label="Start" value={humanizeTimestamp(task.start_date)} />
                      <InfoRow label="Due" value={humanizeTimestamp(task.due_date)} />
                      <InfoRow label="Employee" value={task.employee_display_name} />
                      <InfoRow label="Status" value={<TaskStatusPill status={task.status} />} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default EventDrawerView;
